import { CheckCircle, FileIcon, FileText, ImageIcon } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useGetBlobsQuery } from '@/services/api/blobService';
import {
  useGetTaskStatusesQuery,
  useUpdateTaskMutation,
} from '@/services/api/tasksService';
import { Blob } from '@/services/types/blob-types';
import { CanonicalTaskStatus } from '@/services/types/task-types';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface ClientUploadsReviewProps {
  taskId: string;
  matterId: string;
  /** Blob IDs from the task's metadata.client_portal_blob_ids */
  blobIds: string[];
  onComplete?: () => void;
}

export const ClientUploadsReview: React.FC<ClientUploadsReviewProps> = ({
  taskId,
  matterId,
  blobIds,
  onComplete,
}) => {
  const { data: blobs = [], isLoading } = useGetBlobsQuery(matterId, { skip: !matterId });
  const { data: statuses = [] } = useGetTaskStatusesQuery();
  const [updateTask, { isLoading: isCompleting }] = useUpdateTaskMutation();
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  // Filter to only the blobs tracked in this task's metadata
  const clientBlobs = React.useMemo(() => {
    const idSet = new Set(blobIds);
    return blobs
      .filter((b) => idSet.has(b.id))
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
  }, [blobs, blobIds]);

  const selectedBlob: Blob | undefined = clientBlobs[selectedIndex];
  const isImage = selectedBlob?.file_mime_type?.startsWith('image/');
  const previewUrl = selectedBlob?.pdf_preview_url || selectedBlob?.download_url;

  const handleMarkComplete = async () => {
    const completedStatus = statuses.find(
      (s) => s.canonical_status === CanonicalTaskStatus.COMPLETED,
    );
    if (!completedStatus) {
      toast.error('Could not find Completed status');
      return;
    }
    try {
      await updateTask({
        matterId,
        taskId,
        task: { status_id: completedStatus.id },
      }).unwrap();
      toast.success('Review marked complete');
      onComplete?.();
    } catch {
      toast.error('Failed to complete task');
    }
  };

  if (isLoading) {
    return (
      <div className="py-12 text-center text-sm text-zinc-400">
        Loading documents...
      </div>
    );
  }

  if (clientBlobs.length === 0) {
    return (
      <div className="space-y-3">
        <div className="py-8 text-center text-sm text-zinc-400">
          No new client uploads to review.
        </div>
        <Button
          onClick={handleMarkComplete}
          disabled={isCompleting}
          className="w-full bg-emerald-600 text-white hover:bg-emerald-700"
          size="sm"
        >
          <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
          {isCompleting ? 'Completing...' : 'Mark Review Complete'}
        </Button>
      </div>
    );
  }

  const getFileIcon = (blob: Blob) => {
    if (blob.file_mime_type?.startsWith('image/')) return ImageIcon;
    return FileText;
  };

  const renderPreview = () => {
    if (isImage) {
      return (
        <img
          src={previewUrl}
          alt={selectedBlob?.name}
          className="w-full h-full object-contain bg-white"
        />
      );
    }
    if (previewUrl) {
      return (
        <iframe
          src={previewUrl}
          title={selectedBlob?.name}
          className="w-full h-full border-0"
        />
      );
    }
    return (
      <div className="flex flex-col items-center justify-center h-full text-zinc-400">
        <FileIcon className="w-8 h-8 mb-2" />
        <p className="text-sm">Preview not available</p>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* Split layout: file list left, preview right */}
      <div className="flex gap-3 h-[70vh] min-h-[350px]">
        {/* Left: file list */}
        <div className="w-[200px] shrink-0 overflow-y-auto rounded-lg border border-zinc-200 bg-white">
          {clientBlobs.map((blob, index) => {
            const Icon = getFileIcon(blob);
            const isSelected = index === selectedIndex;
            return (
              <button
                key={blob.id}
                type="button"
                onClick={() => setSelectedIndex(index)}
                className={cn(
                  'w-full text-left px-3 py-2.5 flex items-start gap-2.5 transition-colors border-b border-zinc-100 last:border-b-0',
                  isSelected ? 'bg-zinc-100' : 'hover:bg-zinc-50',
                )}
              >
                <Icon
                  className={cn(
                    'w-4 h-4 mt-0.5 shrink-0',
                    isSelected ? 'text-zinc-700' : 'text-zinc-400',
                  )}
                />
                <div className="min-w-0 flex-1">
                  <p
                    className={cn(
                      'text-xs truncate',
                      isSelected
                        ? 'font-semibold text-zinc-900'
                        : 'font-medium text-zinc-700',
                    )}
                  >
                    {blob.original_filename || blob.name}
                  </p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">
                    {formatDate(blob.created_at)}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Right: preview */}
        <div className="flex-1 rounded-lg border border-zinc-200 overflow-hidden bg-zinc-50">
          {renderPreview()}
        </div>
      </div>

      {/* Complete button */}
      <Button
        onClick={handleMarkComplete}
        disabled={isCompleting}
        className="w-full bg-emerald-600 text-white hover:bg-emerald-700"
        size="sm"
      >
        <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
        {isCompleting
          ? 'Completing...'
          : `Review Complete (${clientBlobs.length} document${clientBlobs.length === 1 ? '' : 's'})`}
      </Button>
    </div>
  );
};
